import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";

import {
  getRecipe,
  getRecipeConsumers,
  getRecipeVersion,
  getRecipeVersions,
  patchRecipeWithVersion,
} from "../api/recipesApi";
import CreateRecipePage from "./CreateRecipePage";

const UNIT_LABELS = {
  g: "g",
  ml: "ml",
  unit: "unidad",
  package: "paquete",
};

function rowsFromResponse(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response)) return response;
  return [];
}

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message)
    ? message.join(" | ")
    : message || "No se pudo cargar la receta.";
}

function mapVersionComponents(version) {
  const ingredients = Array.isArray(version?.ingredients) ? version.ingredients : [];
  const preparedProducts = Array.isArray(version?.preparedProducts)
    ? version.preparedProducts
    : [];

  return [
    ...ingredients.map((line) => ({
      kind: "ingredient",
      group: "Insumos",
      id: line.ingredientId,
      name: line.ingredient?.name || `Insumo #${line.ingredientId}`,
      unit: line.ingredient?.baseUnit || "",
      unitLabel:
        UNIT_LABELS[line.ingredient?.baseUnit] ||
        line.ingredient?.baseUnit ||
        "sin unidad",
      quantity: String(line.quantity ?? ""),
      notes: line.notes || "",
      isAvailable: Boolean(line.ingredient?.isActive),
    })),
    ...preparedProducts.map((line) => ({
      kind: "preparedProduct",
      group: "Elaborados",
      id: line.preparedProductId,
      name:
        line.preparedProduct?.name ||
        `Elaborado #${line.preparedProductId}`,
      unit: line.preparedProduct?.resultUnit || "",
      unitLabel:
        UNIT_LABELS[line.preparedProduct?.resultUnit] ||
        line.preparedProduct?.resultUnit ||
        "sin unidad",
      quantity: String(line.quantity ?? ""),
      notes: line.notes || "",
      isAvailable: Boolean(line.preparedProduct?.isActive),
    })),
  ];
}

export default function EditRecipePage() {
  const navigate = useNavigate();
  const { recipeId } = useParams();
  const numericRecipeId = Number(recipeId);
  const [recipe, setRecipe] = useState(null);
  const [version, setVersion] = useState(null);
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  const loadData = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const [recipeResponse, versionsResponse, consumersResponse] = await Promise.all([
          getRecipe(numericRecipeId, { signal }),
          getRecipeVersions(numericRecipeId, {
            page: 1,
            limit: 1,
            signal,
          }),
          getRecipeConsumers(numericRecipeId, { signal }),
        ]);
        if (signal.aborted) return;

        const selectedVersion = rowsFromResponse(versionsResponse)[0];
        if (!selectedVersion?.id) {
          throw new Error("Esta receta no tiene una versión para editar.");
        }

        const versionResponse = await getRecipeVersion(
          numericRecipeId,
          selectedVersion.id,
          { signal }
        );
        if (signal.aborted) return;

        setRecipe(recipeResponse);
        setVersion(versionResponse);
        setConsumers(Array.isArray(consumersResponse) ? consumersResponse : []);
      } catch (requestError) {
        if (signal.aborted) return;
        setRecipe(null);
        setVersion(null);
        setError(errorToMessage(requestError));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [numericRecipeId]
  );

  useEffect(() => {
    if (!Number.isFinite(numericRecipeId) || numericRecipeId <= 0) return undefined;
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData, numericRecipeId, reloadVersion]);

  if (!Number.isFinite(numericRecipeId) || numericRecipeId <= 0) {
    return <Navigate to="/stock/base-de-datos/recetas" replace />;
  }

  if (loading) {
    return (
      <Stack sx={{ minHeight: 360, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={30} />
        <Typography sx={{ mt: 1.5, fontWeight: 700, color: "neutral.700" }}>
          Cargando receta...
        </Typography>
      </Stack>
    );
  }

  if (error || !recipe || !version) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <StockPageHeader
          title="Editar Receta"
          onBack={() => navigate("/stock/base-de-datos/recetas")}
        />
        <Box sx={{ p: 3, borderRadius: "22px", bgcolor: "neutral.300" }}>
          <Alert severity="error">{error || "No se pudo cargar la receta."}</Alert>
          <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => navigate("/stock/base-de-datos/recetas")}>Volver</Button>
            <Button variant="contained" onClick={() => setReloadVersion((value) => value + 1)}>
              Reintentar
            </Button>
          </Stack>
        </Box>
      </Stack>
    );
  }

  const initialValues = {
    name: recipe.name,
    sector: recipe.sector,
    isActive: Boolean(recipe.isActive),
    portionQuantity: version.portionQuantity,
    notes: version.notes || "",
    components: mapVersionComponents(version),
  };

  async function handleSave(payload) {
    await patchRecipeWithVersion(numericRecipeId, version.id, {
      recipe: {
        name: payload.name,
        sector: payload.sector,
        isActive: payload.isActive,
      },
      version: {
        ...payload.version,
        isActive: payload.isActive ? true : Boolean(version.isActive),
      },
    });
  }

  return (
    <CreateRecipePage
      initialValues={initialValues}
      pageTitle="Editar Receta"
      showStatus
      confirmOnCancel
      successMessage="La receta se actualizó correctamente."
      onSave={handleSave}
      sharedConsumers={consumers}
    />
  );
}
